import {
	ApiResponse,
	UpdateVolunteersAboutRequest,
	VolunteersAboutFormData,
	VolunteersAboutGetApiResponse,
} from "../context/VolunteersAboutContext/constants";
import { axiosInstance } from "../utils/axiosInstance";

const ENDPOINT = "/cms/volunteers-about";

const LOCALIZABLE_PREFIX = "volunteersAboutLocalizable";

interface LocalizableFormFields {
	volunteersAboutLocalizableId?: string;
	culture: string;
	title?: string;
	description?: string;
	image?: File | null;
}

// The backend stores both cultures in one folder named after the uploaded file, so the same file name (or a re-upload
// of it) would overwrite the other image on the CDN; the culture and upload time keep every image at its own URL.
const toUploadFileName = (culture: string, file: File) => `${culture}-${Date.now()}-${file.name}`;

const appendLocalizable = (formData: FormData, index: number, { image, ...fields }: LocalizableFormFields) => {
	const prefix = `${LOCALIZABLE_PREFIX}[${index}]`;
	const texts: [string, string | undefined][] = [
		["volunteersAboutLocalizableId", fields.volunteersAboutLocalizableId || undefined],
		["culture", fields.culture],
		["title", fields.title?.trim()],
		["description", fields.description?.trim()],
	];

	texts.forEach(([name, value]) => {
		if (value !== undefined) {
			formData.append(`${prefix}.${name}`, value);
		}
	});
	if (image) {
		formData.append(`${prefix}.image`, image, toUploadFileName(fields.culture, image));
	}
};

const toFormData = (localizables: LocalizableFormFields[]): FormData => {
	const formData = new FormData();
	localizables.forEach((item, index) => appendLocalizable(formData, index, item));
	return formData;
};

export const buildAddVolunteersAboutFormData = (data: VolunteersAboutFormData) => toFormData(data.volunteersAboutLocalizable);

// Only the changed fields of a culture are sent; the backend keeps the others as they are.
export const buildUpdateVolunteersAboutFormData = (request: UpdateVolunteersAboutRequest) => toFormData(request.volunteersAboutLocalizable);

export class VolunteersAboutService {
	static async getVolunteersAbout(): Promise<VolunteersAboutGetApiResponse> {
		const response = await axiosInstance.get(ENDPOINT);
		return response.data;
	}

	static async addVolunteersAbout(data: VolunteersAboutFormData): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.post(ENDPOINT, buildAddVolunteersAboutFormData(data));
		return response.data;
	}

	static async updateVolunteersAbout(request: UpdateVolunteersAboutRequest): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.patch(ENDPOINT, buildUpdateVolunteersAboutFormData(request));
		return response.data;
	}

	static async removeVolunteersAbout(): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.delete(ENDPOINT);
		return response.data;
	}
}
